/**
 * Deep-scan intercom catalog — 50+ verified entries across 4 major IP/door-
 * entry brands (Aiphone, Akuvox, Comelit, 2N).
 *
 * Every entry below is grounded in a primary manufacturer source. URLs in
 * `datasheetUrl` and the `// SRC:` comment immediately above each entry
 * point at the page used. Where a spec is not on the cited page it is
 * omitted (per existing catalog convention) rather than guessed.
 *
 * Sources fetched (Aiphone product pages on aiphone.com, Akuvox
 * productsDisp on akuvox.com, Comelit staticpro datasheet PDFs on
 * staticpro.comelitgroup.com, 2N product/manual pages and datasheets on
 * 2n.com / wiki.2n.com). The 2N main site blocks unauthenticated WebFetch
 * (403) — the verified numbers below were lifted from the public search
 * snippets returned by Google site:2n.com, which surface the same
 * datasheet text. Each is cross-referenced to the datasheet/manual URL.
 *
 * IntercomSpec shape comes from equipmentCatalog.ts:
 *   type:    'audio-analog' | 'audio-video-2wire' | 'ip-sip' | 'ip-proprietary'
 *   side:    'outdoor-station' | 'indoor-monitor'
 *   cameraMP, monitorInches, callButtons, ipRating, poe, accessControl[]
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

// Common datasheet URL prefixes (kept short — full URLs in SRC comments)
const AIPHONE_PRODUCTS = 'https://www.aiphone.com/products';
const AKUVOX_PRODUCTS = 'https://www.akuvox.com/productsDisp';
const COMELIT_DATASHEETS = 'https://staticpro.comelitgroup.com';
const TWO_N_DATASHEETS = 'https://www.2n.com';

export const INTERCOMS_DEEP: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════
  // A. AIPHONE — IX | IXG | GT | JO series
  // ═══════════════════════════════════════════════════════════════════

  // ── IX Series (IP video, PoE 802.3af, SIP/ONVIF) ────────────────────

  // SRC: https://www.aiphone.com/products/ix-dv — 1.23 MP, IP54, IK08, PoE 802.3af, SIP, ONVIF S, surface mount
  {
    id: 'aiphone-ix-dv',
    manufacturer: 'Aiphone',
    model: 'IX-DV',
    category: 'intercom',
    tagline: 'IX series IP video door station — 1.23 MP, IP54/IK08, PoE, SIP+ONVIF',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ix-dv`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 1.23,
      callButtons: 1,
      ipRating: 'IP54/IK08',
      poe: true,
    },
  },

  // SRC: https://www.aiphone.com/products/ix-dvf-2ra — 1.23 MP, IP65/IK08 stainless, PoE, ADA emergency + std call, SIP/ONVIF
  {
    id: 'aiphone-ix-dvf-2ra',
    manufacturer: 'Aiphone',
    model: 'IX-DVF-2RA',
    category: 'intercom',
    tagline: 'Vandal-resistant IP video — 1.23 MP, IP65/IK08, ADA emergency + std call',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ix-dvf-2ra`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 1.23,
      callButtons: 2,
      ipRating: 'IP65/IK08',
      poe: true,
    },
  },

  // SRC: https://www.aiphone.com/products/ix-ea — 1.23 MP, weather-resistant, PoE 802.3af, surface 1-gang, SIP/ONVIF
  {
    id: 'aiphone-ix-ea',
    manufacturer: 'Aiphone',
    model: 'IX-EA',
    category: 'intercom',
    tagline: 'IX entry door station — 1.23 MP, weather-resistant, PoE, surface 1-gang',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ix-ea`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 1.23,
      callButtons: 1,
      poe: true,
    },
  },

  // SRC: https://www.aiphone.com/products/ix-ssa — Flush IP audio door station, PoE 802.3af, IP65/IK08
  {
    id: 'aiphone-ix-ssa',
    manufacturer: 'Aiphone',
    model: 'IX-SSA',
    category: 'intercom',
    tagline: 'IX flush IP audio door station — IP65/IK08, PoE',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ix-ssa`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      callButtons: 1,
      ipRating: 'IP65/IK08',
      poe: true,
    },
  },

  // SRC: https://www.aiphone.com/products/ix-mv7-w — 7" TFT 800x400, PoE 802.3af class 0, hands-free or PTT, SIP/ONVIF
  {
    id: 'aiphone-ix-mv7-w',
    manufacturer: 'Aiphone',
    model: 'IX-MV7-W',
    category: 'intercom',
    tagline: 'IX master station — 7" touchscreen, PoE, hands-free or PTT',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ix-mv7-w`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://www.aiphone.com/products/ix-mv7-hw — 7" TFT 800x400, handset, PoE 802.3af, built-in camera w/ privacy shutter
  {
    id: 'aiphone-ix-mv7-hw-jp',
    manufacturer: 'Aiphone',
    model: 'IX-MV7-HW-JP',
    category: 'intercom',
    tagline: 'IX master station — 7" touchscreen + handset, PoE, built-in camera',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ix-mv7-hw`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://www.aiphone.com/products/ix-mv7-hb — 7" LCD, integrated handset, PoE 802.3af class 0, full-duplex
  {
    id: 'aiphone-ix-mv7-hb',
    manufacturer: 'Aiphone',
    model: 'IX-MV7-HB',
    category: 'intercom',
    tagline: 'IX master station — 7" touchscreen + handset (black), full-duplex',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ix-mv7-hb`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // ── IXG Series (multi-tenant, HID integration) ──────────────────────

  // SRC: https://www.aiphone.com/products/ixg-dm7-hid — 7" TFT 800x400, IP65/IK08, built-in HID reader (HF 13.56 + LF 125 + BLE)
  {
    id: 'aiphone-ixg-dm7-hid',
    manufacturer: 'Aiphone',
    model: 'IXG-DM7-HID',
    category: 'intercom',
    tagline: 'IXG multi-tenant entrance — 7" display, IP65/IK08, HID prox + BLE',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ixg-dm7-hid`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 7,
      ipRating: 'IP65/IK08',
      poe: true,
      accessControl: ['mifare', 'em-marin'],
    },
  },

  // SRC: https://www.aiphone.com/products/ixg-2c7 — 7" LCD 800x400, PoE 802.3af, G.711/H.264, full-duplex
  {
    id: 'aiphone-ixg-2c7',
    manufacturer: 'Aiphone',
    model: 'IXG-2C7',
    category: 'intercom',
    tagline: 'IXG IP video tenant station — 7" touchscreen, PoE',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ixg-2c7`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://www.aiphone.com/products/ixg-mk — 7" TFT 800x400, PoE 802.3af class 0
  {
    id: 'aiphone-ixg-mk',
    manufacturer: 'Aiphone',
    model: 'IXG-MK',
    category: 'intercom',
    tagline: 'IXG guard station — 7" touchscreen, PoE',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/ixg-mk`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // ── GT Series (modular apartment, Simplebus-style 2-wire) ───────────

  // SRC: https://www.aiphone.com/products/gt-db-vn — audio + NFC module for GT modular panel, VIGIK, 24V DC from GT-BC
  {
    id: 'aiphone-gt-db-vn',
    manufacturer: 'Aiphone',
    model: 'GT-DB-VN',
    category: 'intercom',
    tagline: 'GT modular audio module + NFC reader — Card type A/F, VIGIK',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/gt-db-vn`,
    specs: {
      type: 'audio-video-2wire',
      side: 'outdoor-station',
      accessControl: ['mifare'],
    },
  },

  // SRC: https://www.aiphone.com/products/gt-vb — 1/4" CMOS, 170°H x 100°V, digital PTZ, IP43
  {
    id: 'aiphone-gt-vb',
    manufacturer: 'Aiphone',
    model: 'GT-VB',
    category: 'intercom',
    tagline: 'GT modular camera module — 1/4" CMOS, 170° H FOV, IP43',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/gt-vb`,
    specs: {
      type: 'audio-video-2wire',
      side: 'outdoor-station',
      ipRating: 'IP43',
    },
  },

  // SRC: https://www.aiphone.com/products/gt-mkb-n — 3.5" screen, built-in NFC reader, 24V DC 250mA
  {
    id: 'aiphone-gt-mkb-n',
    manufacturer: 'Aiphone',
    model: 'GT-MKB-N',
    category: 'intercom',
    tagline: 'GT guard station — 3.5" screen, handset, built-in NFC reader',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/gt-mkb-n`,
    specs: {
      type: 'audio-video-2wire',
      side: 'indoor-monitor',
      monitorInches: 3.5,
      accessControl: ['mifare'],
    },
  },

  // SRC: https://www.aiphone.com/products/gt-1c7 — 7" LCD, hands-free/PTT, 24V DC from GT-BC, digital PTZ
  {
    id: 'aiphone-gt-1c7',
    manufacturer: 'Aiphone',
    model: 'GT-1C7',
    category: 'intercom',
    tagline: 'GT video tenant station — 7" LCD, hands-free, digital PTZ',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/gt-1c7`,
    specs: {
      type: 'audio-video-2wire',
      side: 'indoor-monitor',
      monitorInches: 7,
    },
  },

  // SRC: https://www.aiphone.com/products/gt-1c7w — 7" TFT touch + WiFi (802.11b/g/n), app-enabled, 24V DC ~300mA
  {
    id: 'aiphone-gt-1c7w',
    manufacturer: 'Aiphone',
    model: 'GT-1C7W',
    category: 'intercom',
    tagline: 'GT WiFi tenant station — 7" touch, mobile-app enabled (up to 8 devices)',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/gt-1c7w`,
    specs: {
      type: 'audio-video-2wire',
      side: 'indoor-monitor',
      monitorInches: 7,
    },
  },

  // SRC: https://www.aiphone.com/products/gt-1m3 — 3.5" LCD, hands-free/PTT, 24V DC, digital PTZ on call
  {
    id: 'aiphone-gt-1m3',
    manufacturer: 'Aiphone',
    model: 'GT-1M3',
    category: 'intercom',
    tagline: 'GT compact video tenant station — 3.5" LCD, hands-free',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/gt-1m3`,
    specs: {
      type: 'audio-video-2wire',
      side: 'indoor-monitor',
      monitorInches: 3.5,
    },
  },

  // SRC: https://www.aiphone.com/products/gt-1a — hands-free audio tenant station, 24V DC from GT-BC
  {
    id: 'aiphone-gt-1a',
    manufacturer: 'Aiphone',
    model: 'GT-1A',
    category: 'intercom',
    tagline: 'GT audio tenant station — hands-free, surface 3-gang mount',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/gt-1a`,
    specs: {
      type: 'audio-analog',
      side: 'indoor-monitor',
    },
  },

  // SRC: https://www.aiphone.com/products/gt-1d — handset audio tenant station, 24V DC, self-extinguishing ABS
  {
    id: 'aiphone-gt-1d',
    manufacturer: 'Aiphone',
    model: 'GT-1D',
    category: 'intercom',
    tagline: 'GT audio handset tenant station — privacy handset, ABS housing',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/gt-1d`,
    specs: {
      type: 'audio-analog',
      side: 'indoor-monitor',
    },
  },

  // ── JO Series (entry-level analog video, app-enabled) ───────────────

  // SRC: https://www.aiphone.com/products/jo-1mdw — 7" TFT touch, WiFi 802.11b/g/n 2.4GHz, IP54, 18V DC from PS-1820UL, up to 8 mobile devices
  {
    id: 'aiphone-jo-1mdw',
    manufacturer: 'Aiphone',
    model: 'JO-1MDW',
    category: 'intercom',
    tagline: 'JO app-enabled monitor — 7" touch, WiFi, IP54 housing, call recording',
    verified: true,
    datasheetUrl: `${AIPHONE_PRODUCTS}/jo-1mdw`,
    specs: {
      type: 'audio-video-2wire',
      side: 'indoor-monitor',
      monitorInches: 7,
      ipRating: 'IP54',
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // B. AKUVOX — SIP IP intercoms (door phones + indoor monitors)
  // ═══════════════════════════════════════════════════════════════════

  // ── Door phones (outdoor stations) ──────────────────────────────────

  // SRC: https://www.akuvox.com/productsDisp?pid=11 — "Palm-Size", silver aluminum, 1 cam + 1 btn, dual-freq RFID, PoE, IP65, ONVIF
  {
    id: 'akuvox-r20a',
    manufacturer: 'Akuvox',
    model: 'R20A',
    category: 'intercom',
    tagline: 'Palm-size SIP video doorphone — 1 button, dual-freq RFID, IP65, PoE',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=11`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['em-marin', 'mifare'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=16 — 4.3" TFT, numeric keypad, dual-freq RFID, IP65/IK06, PoE, H.265
  {
    id: 'akuvox-r28',
    manufacturer: 'Akuvox',
    model: 'R28',
    category: 'intercom',
    tagline: 'SIP video doorphone — 4.3" TFT + keypad, dual-freq RFID, IP65/IK06',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=16`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 4.3,
      ipRating: 'IP65/IK06',
      poe: true,
      accessControl: ['em-marin', 'mifare', 'keypad'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=17 — Android, 7" touch, AI face recog, dual-freq RFID, IP65/IK06, PoE, BLE on R29C
  {
    id: 'akuvox-r29c',
    manufacturer: 'Akuvox',
    model: 'R29C',
    category: 'intercom',
    tagline: 'Android SIP doorphone — 7" touch, AI face recognition, dual cameras',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=17`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 7,
      ipRating: 'IP65/IK06',
      poe: true,
      accessControl: ['em-marin', 'mifare'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=17 — R29CT adds fingerprint reader
  {
    id: 'akuvox-r29ct',
    manufacturer: 'Akuvox',
    model: 'R29CT',
    category: 'intercom',
    tagline: 'Android SIP doorphone — 7" touch, face + fingerprint + RFID',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=17`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 7,
      ipRating: 'IP65/IK06',
      poe: true,
      accessControl: ['em-marin', 'mifare', 'fingerprint'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=43 — E12S Standard PoE, NFC, IP65, ONVIF, Wiegand
  {
    id: 'akuvox-e12s',
    manufacturer: 'Akuvox',
    model: 'E12S',
    category: 'intercom',
    tagline: 'Compact SIP video doorphone — 1 button, NFC, IP65, PoE, Wiegand',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=43`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=43 — E12W: same chassis + WiFi 802.11b/g/n 2.4GHz
  {
    id: 'akuvox-e12w',
    manufacturer: 'Akuvox',
    model: 'E12W',
    category: 'intercom',
    tagline: 'Compact SIP video doorphone — WiFi 2.4 GHz, NFC, IP65',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=43`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      callButtons: 1,
      ipRating: 'IP65',
      accessControl: ['mifare'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=39 — 5" touchscreen, 1 cam, NFC 13.56 MHz, IP65, PoE, face recog
  {
    id: 'akuvox-e16c',
    manufacturer: 'Akuvox',
    model: 'E16C',
    category: 'intercom',
    tagline: 'SIP doorphone — 5" touch, NFC, face recognition, IP65, PoE',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=39`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 5,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=54 — 7" touch, dual cameras (face recog), dual-freq RFID, IP65, PoE, Linux
  {
    id: 'akuvox-e18c',
    manufacturer: 'Akuvox',
    model: 'E18C',
    category: 'intercom',
    tagline: 'Smart SIP doorphone — 7" touch, dual cams + face recog, dual-freq RFID, IP65',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=54`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 7,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['em-marin', 'mifare'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=53 — 4" touch, dual cams, IP65/IK10, PoE+, Linux, dual-freq RFID
  {
    id: 'akuvox-x912s',
    manufacturer: 'Akuvox',
    model: 'X912S',
    category: 'intercom',
    tagline: 'Vandal-resistant SIP doorphone — 4" touch, IP65/IK10, PoE+, dual-freq RFID',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=53`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 4,
      ipRating: 'IP65/IK10',
      poe: true,
      accessControl: ['em-marin', 'mifare', 'keypad'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=53 — X912K physical keypad variant, HID iClass option, IP65/IK08
  {
    id: 'akuvox-x912k',
    manufacturer: 'Akuvox',
    model: 'X912K',
    category: 'intercom',
    tagline: 'Vandal-resistant SIP doorphone — physical keypad, HID iClass, IP65/IK08',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=53`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 4,
      ipRating: 'IP65/IK08',
      poe: true,
      accessControl: ['mifare', 'keypad'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=37 — Android, 8" touch, dual cameras, IP66/IK10, PoE+, BLE, dual-freq RFID
  {
    id: 'akuvox-x915s',
    manufacturer: 'Akuvox',
    model: 'X915S',
    category: 'intercom',
    tagline: 'Premium Android SIP doorphone — 8" touch, IP66/IK10, PoE+, BLE',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=37`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 8,
      ipRating: 'IP66/IK10',
      poe: true,
      accessControl: ['em-marin', 'mifare'],
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=37 — X915 HID iClass variant (13.56 MHz HID)
  {
    id: 'akuvox-x915-hid',
    manufacturer: 'Akuvox',
    model: 'X915-HID',
    category: 'intercom',
    tagline: 'Premium SIP doorphone with HID iClass — 8" touch, IP66/IK10',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=37`,
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      monitorInches: 8,
      ipRating: 'IP66/IK10',
      poe: true,
      accessControl: ['mifare'],
    },
  },

  // ── Indoor monitors ─────────────────────────────────────────────────

  // SRC: https://www.akuvox.com/productsDisp?pid=20 — C313S 7" capacitive, Linux, PoE; C313W adds WiFi
  {
    id: 'akuvox-c313s',
    manufacturer: 'Akuvox',
    model: 'C313S',
    category: 'intercom',
    tagline: 'Compact SIP indoor monitor — 7" capacitive, PoE, Linux',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=20`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=20 — C313W — same chassis + WiFi
  {
    id: 'akuvox-c313w',
    manufacturer: 'Akuvox',
    model: 'C313W',
    category: 'intercom',
    tagline: 'Compact SIP indoor monitor — 7" capacitive + WiFi, PoE',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=20`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=94 — C316S Android 12, 7", PoE; C316W adds WiFi
  {
    id: 'akuvox-c316s',
    manufacturer: 'Akuvox',
    model: 'C316S',
    category: 'intercom',
    tagline: 'Android 12 SIP indoor monitor — 7" touch, PoE',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=94`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=44 — C319S Android 9, 10" touch, PoE, telecare integration, voice assistant
  {
    id: 'akuvox-c319s',
    manufacturer: 'Akuvox',
    model: 'C319S',
    category: 'intercom',
    tagline: 'Android 9 SIP indoor monitor — 10" touch, PoE, voice assistant',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=44`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 10,
      poe: true,
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=73 — IT88S Android, 10" screen, PoE; IT88A adds WiFi/BT/camera
  {
    id: 'akuvox-it88a',
    manufacturer: 'Akuvox',
    model: 'IT88A',
    category: 'intercom',
    tagline: 'Premium Android SIP indoor monitor — 10", PoE, WiFi, Bluetooth, camera',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=73`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 10,
      poe: true,
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=105 — X937 15.6" Android 14, PoE, surveillance+intercom AI monitor, HDMI-in NVR
  {
    id: 'akuvox-x937a',
    manufacturer: 'Akuvox',
    model: 'X937A',
    category: 'intercom',
    tagline: 'Android 14 AI indoor monitor — 15.6", PoE, surveillance + intercom + LLM voice',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=105`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 15.6,
      poe: true,
    },
  },

  // SRC: https://www.akuvox.com/productsDisp?pid=87 — S560 handset audio answering unit, Linux, PoE
  {
    id: 'akuvox-s560',
    manufacturer: 'Akuvox',
    model: 'S560',
    category: 'intercom',
    tagline: 'SIP audio handset — basic intercom + door unlock, PoE, Linux',
    verified: true,
    datasheetUrl: `${AKUVOX_PRODUCTS}?pid=87`,
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      poe: true,
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // C. COMELIT — Simplebus 2 + ViP (IP) systems
  // ═══════════════════════════════════════════════════════════════════

  // ── Ultra (premium IP touchscreen entrance panel) ───────────────────

  // SRC: https://staticpro.comelitgroup.com/en-us/datasheets/18832-ut8090.pdf
  // ViP (IP) system, 5" capacitive 800x480, 1/3" CMOS 160°x100° FOV, PoE 802.3af class 0
  // 14W max, RFID Mifare Classic, Wiegand out, BT, RS485
  {
    id: 'comelit-ut8090',
    manufacturer: 'Comelit',
    model: 'UT8090 (Ultra)',
    category: 'intercom',
    tagline: 'Ultra ViP entrance panel — 5" capacitive touch, 160° FOV, Mifare RFID, PoE, BT',
    verified: true,
    datasheetUrl: `${COMELIT_DATASHEETS}/en-us/datasheets/18832-ut8090.pdf`,
    specs: {
      type: 'ip-proprietary', // Comelit ViP
      side: 'outdoor-station',
      monitorInches: 5,
      callButtons: 0, // digital — virtual buttons + keypad
      poe: true,
      accessControl: ['mifare'],
    },
  },

  // ── Quadra entrance panels ──────────────────────────────────────────

  // SRC: https://staticpro.comelitgroup.com/en-us/datasheets/14379-4893m.pdf
  // Simplebus 2 system, 4 buttons, 1/4" CMOS 85°x72°, bus-powered (33VDC), IP54/IK08
  {
    id: 'comelit-4893m',
    manufacturer: 'Comelit',
    model: '4893M (Quadra SB2)',
    category: 'intercom',
    tagline: 'Quadra Simplebus 2 entrance panel — die-cast aluminium, 4 buttons, IP54/IK08',
    verified: true,
    datasheetUrl: `${COMELIT_DATASHEETS}/en-us/datasheets/14379-4893m.pdf`,
    specs: {
      type: 'audio-video-2wire', // Simplebus 2
      side: 'outdoor-station',
      callButtons: 4,
      ipRating: 'IP54/IK08',
    },
  },

  // SRC: https://staticpro.comelitgroup.com/en-us/datasheets/14409-4899m.pdf
  // ViP system, 1/4" CMOS 100°x55° HD 720p H.264, PoE 802.3af (36–57VDC), 1–4 buttons via DIP, IP54/IK08
  {
    id: 'comelit-4899m',
    manufacturer: 'Comelit',
    model: '4899M (Quadra ViP)',
    category: 'intercom',
    tagline: 'Quadra ViP entrance panel — HD 720p H.264, 1–4 buttons, PoE, IP54/IK08',
    verified: true,
    datasheetUrl: `${COMELIT_DATASHEETS}/en-us/datasheets/14409-4899m.pdf`,
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      callButtons: 4,
      ipRating: 'IP54/IK08',
      poe: true,
    },
  },

  // ── Switch entrance panels (316 stainless) ──────────────────────────

  // SRC: https://staticpro.comelitgroup.com/en-us/datasheets/14083-3453s.pdf
  // Simplebus 1 system, 8" capacitive 800x480, 1/3" CCD 87°x59°, RFID, RJ45+RS485, 33VDC ext, IP54/IK08
  {
    id: 'comelit-3453s',
    manufacturer: 'Comelit',
    model: '3453S (Switch 316 Touch)',
    category: 'intercom',
    tagline: 'Switch 316 stainless A/V entrance — 8" capacitive touch, RFID, IP54/IK08',
    verified: true,
    datasheetUrl: `${COMELIT_DATASHEETS}/en-us/datasheets/14083-3453s.pdf`,
    specs: {
      type: 'audio-video-2wire',
      side: 'outdoor-station',
      monitorInches: 8,
      ipRating: 'IP54/IK08',
      accessControl: ['mifare'],
    },
  },

  // ── Mini indoor monitors ────────────────────────────────────────────

  // SRC: https://staticpro.comelitgroup.com/en-us/datasheets/11979-6741w.pdf
  // SB2 Mini hands-free WiFi, 4.3" LCD 16:9 480x272, bus-powered, Alexa + Google
  {
    id: 'comelit-6741w',
    manufacturer: 'Comelit',
    model: '6741W (Mini hands-free WiFi)',
    category: 'intercom',
    tagline: 'Mini Simplebus 2 hands-free monitor — 4.3" LCD, WiFi, Alexa/Google',
    verified: true,
    datasheetUrl: `${COMELIT_DATASHEETS}/en-us/datasheets/11979-6741w.pdf`,
    specs: {
      type: 'audio-video-2wire',
      side: 'indoor-monitor',
      monitorInches: 4.3,
    },
  },

  // SRC: https://staticpro.comelitgroup.com/storage/2021/03/27192/mt-6701-en.pdf
  // SB2 Mini handset, 4.3" 16:9, soft-touch buttons, Simplebus 2 only
  {
    id: 'comelit-6701w',
    manufacturer: 'Comelit',
    model: '6701W (Mini handset)',
    category: 'intercom',
    tagline: 'Mini Simplebus 2 handset monitor — 4.3" LCD, 4 soft-touch buttons',
    verified: true,
    datasheetUrl: `${COMELIT_DATASHEETS}/storage/2021/03/27192/mt-6701-en.pdf`,
    specs: {
      type: 'audio-video-2wire',
      side: 'indoor-monitor',
      monitorInches: 4.3,
    },
  },

  // ── Maxi 7" indoor monitors ─────────────────────────────────────────

  // SRC: https://staticpro.comelitgroup.com/en-gb/datasheets/14300-6842w.pdf
  // ViP, 7" 800x480 resistive touch, PoE 802.3af, WiFi 802.11b/g/n 2.4GHz, full-duplex + induction loop
  {
    id: 'comelit-6842w',
    manufacturer: 'Comelit',
    model: '6842W (Maxi ViP WiFi)',
    category: 'intercom',
    tagline: 'Maxi ViP monitor — 7" resistive touch, PoE, WiFi, induction loop',
    verified: true,
    datasheetUrl: `${COMELIT_DATASHEETS}/en-gb/datasheets/14300-6842w.pdf`,
    specs: {
      type: 'ip-proprietary',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://staticpro.comelitgroup.com/en-ie/datasheets/14603-6813w.pdf
  // ViP, 7" 1024x600 capacitive touch, Android, PoE 802.3af, WiFi 802.11b/g/n
  {
    id: 'comelit-6813w',
    manufacturer: 'Comelit',
    model: '6813W (Maxi Android ViP)',
    category: 'intercom',
    tagline: 'Maxi ViP Android monitor — 7" capacitive, PoE, WiFi, third-party apps',
    verified: true,
    datasheetUrl: `${COMELIT_DATASHEETS}/en-ie/datasheets/14603-6813w.pdf`,
    specs: {
      type: 'ip-proprietary',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://staticpro.comelitgroup.com/storage/2020/10/26158/mt-6801-en.pdf
  // Simplebus 2 system, 7" colour screen, 2-wire
  {
    id: 'comelit-6801w',
    manufacturer: 'Comelit',
    model: '6801W (Maxi SB2)',
    category: 'intercom',
    tagline: 'Maxi Simplebus 2 monitor — 7" colour screen, 2-wire',
    verified: true,
    datasheetUrl: `${COMELIT_DATASHEETS}/storage/2020/10/26158/mt-6801-en.pdf`,
    specs: {
      type: 'audio-video-2wire',
      side: 'indoor-monitor',
      monitorInches: 7,
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // D. 2N — IP intercoms (Verso, Force, Solo, Vario, Style) + indoor
  // ═══════════════════════════════════════════════════════════════════
  // NB: 2n.com blocks WebFetch (403); each entry below is grounded in
  // the public Google snippet of the cited 2N datasheet/manual URL.

  // SRC: https://wiki.2n.com/hipve/inst/latest/en/4-technicke-parametry
  // Modular outdoor IP intercom, main unit IP54/IK08 (touch display IK07),
  // PoE 802.3af, camera 1280×960 JPEG / 640×480 video, SIP
  {
    id: '2n-ip-verso',
    manufacturer: '2N',
    model: 'IP Verso',
    category: 'intercom',
    tagline: 'Modular IP intercom — 1.2 MP camera, IP54/IK08, PoE, 20+ modules, SIP',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-GB/download/ip_verso_datasheet_a4_enpdf',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 1.2,
      ipRating: 'IP54/IK08',
      poe: true,
      accessControl: ['mifare', 'em-marin', 'keypad', 'fingerprint'],
    },
  },

  // SRC: https://www.2n.com/en-US/products/intercoms/2n-ip-verso-2/
  // IP Verso 2.0: modular, nickel or black finish, up to 20 modules combined
  // Datasheet snippet: same IP54/IK08 main unit, full-HD optional camera, PoE
  {
    id: '2n-ip-verso-2',
    manufacturer: '2N',
    model: 'IP Verso 2.0',
    category: 'intercom',
    tagline: 'Modular IP intercom 2.0 — nickel/black, up to 20 modules, PoE, SIP',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/intercoms/2n-ip-verso-2/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      ipRating: 'IP54/IK08',
      poe: true,
      accessControl: ['mifare', 'em-marin', 'keypad', 'fingerprint'],
    },
  },

  // SRC: https://wiki.2n.com/hipfo/inst/latest/en/4-technicke-parametry
  // 2N IP Force: IP69K + IK10, HD camera 1280×960, PoE 802.3af class 0 (12.95W)
  // Part No. 9151101W (1-button base variant)
  {
    id: '2n-ip-force-1btn',
    manufacturer: '2N',
    model: 'IP Force — 1 button',
    category: 'intercom',
    tagline: 'Toughest IP intercom — IP69K/IK10, HD camera, PoE 802.3af, 1 call button',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/download/ip_force_datasheet_letter_uspdf',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 1.2,
      callButtons: 1,
      ipRating: 'IP69K/IK10',
      poe: true,
    },
  },

  // SRC: https://wiki.2n.com/hipfo/inst/latest/en/4-technicke-parametry
  // 2N IP Force 4-button variant with HD camera (referenced as "2N IP Force - 4 Buttons" product detail)
  {
    id: '2n-ip-force-4btn',
    manufacturer: '2N',
    model: 'IP Force — 4 buttons',
    category: 'intercom',
    tagline: 'Toughest IP intercom — IP69K/IK10, HD camera, 4 call buttons',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/2n-ip-force-4-buttons',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 1.2,
      callButtons: 4,
      ipRating: 'IP69K/IK10',
      poe: true,
    },
  },

  // SRC: https://www.2n.com/en-GB/products/intercoms/2n-ip-solo/
  // IP Solo: 1 button + camera, JPEG 1280×960, video call 640×480, 128°H/95°V/134°D FOV, night vision
  // Power 24 W for main unit + connected modules
  {
    id: '2n-ip-solo',
    manufacturer: '2N',
    model: 'IP Solo',
    category: 'intercom',
    tagline: 'Stylish IP intercom — HD camera, single call button, ideal for home automation',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-GB/download/ip_solo_datasheet_a4_enpdf',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 1.2,
      callButtons: 1,
      poe: true,
    },
  },

  // SRC: https://www.2n.com/en-GB/download/ip_vario_datasheet_uspdf + 9137160KDU
  // IP Vario 6 buttons + keypad + display, IP53 (w/ roof) / IP50 (no roof),
  // PoE 10V class, SIP 2.0, ONVIF, dim 210×100×29mm
  {
    id: '2n-ip-vario',
    manufacturer: '2N',
    model: 'IP Vario',
    category: 'intercom',
    tagline: 'Apartment IP intercom — graphic display, keypad, 6 buttons, PoE, ONVIF',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-GB/download/ip_vario_datasheet_uspdf',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      callButtons: 6,
      ipRating: 'IP53', // with roof accessory
      poe: true,
      accessControl: ['keypad'],
    },
  },

  // SRC: https://www.2n.com/en-US/products/intercoms/2n-ip-style/
  // 2N IP Style: 10" touch display, AXIS ARTPEC-7, full-HD camera, WaveKey (BLE)
  // Wiki: https://wiki.2n.com/hipstyim/inst/latest/en/2-popis-a-instalace/2-3-elektricka-instalace
  {
    id: '2n-ip-style',
    manufacturer: '2N',
    model: 'IP Style',
    category: 'intercom',
    tagline: 'Premium IP intercom — 10" touch, full-HD camera, AXIS ARTPEC-7, BLE WaveKey',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/intercoms/2n-ip-style/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      cameraMP: 2,
      monitorInches: 10,
      poe: true,
      accessControl: ['mifare'],
    },
  },

  // SRC: https://www.2n.com/-/media/Files/Manuals/1/7/8/7/9/17879/2_44/17879-2_44-en-t-pdf-public-Indoor_Compact-en.pdf
  // 4.3" colour 480×272, 12V DC or PoE 802.3af, SIP, recommended 12V/1A
  {
    id: '2n-indoor-compact',
    manufacturer: '2N',
    model: 'Indoor Compact',
    category: 'intercom',
    tagline: 'Compact answering unit — 4.3" colour, PoE 802.3af, SIP',
    verified: true,
    datasheetUrl: 'https://www.2n.com/cs-CZ/download/indoor_compact_datasheet_a4_enpdf',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 4.3,
      poe: true,
    },
  },

  // SRC: https://wiki.2n.com/download/attachments/59769113/2N_Indoor_Touch_2.0_User_guide_EN_4.7.1.pdf
  // Indoor Touch 2.0 (P/N 91378375): 7" 1024×600, Android 6.0, PoE 802.3af, SIP, H.264
  // NB: discontinued 2025-12-31 per manufacturer
  {
    id: '2n-indoor-touch-2',
    manufacturer: '2N',
    model: 'Indoor Touch 2.0',
    category: 'intercom',
    tagline: 'Answering unit — 7" Android touch, PoE 802.3af, SIP, home automation ready',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-GB/download/indoor_touch_2_datasheet_a4_enpdf',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://wiki.2n.com/usgu/idview/latest/en/7-technicke-parametry
  // Indoor View: 7" 1024×600, PoE 802.3af
  {
    id: '2n-indoor-view',
    manufacturer: '2N',
    model: 'Indoor View',
    category: 'intercom',
    tagline: 'Answering unit — 7" display, PoE 802.3af, SIP',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/indoor-stations/2n-indoor-view/',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      monitorInches: 7,
      poe: true,
    },
  },

  // SRC: https://www.2n.com/en-US/products/indoor-stations/2n-indoor-talk/
  // Touchscreen audio answering unit, 12V DC or PoE 802.3af, induction loop, Ethernet LAN
  {
    id: '2n-indoor-talk',
    manufacturer: '2N',
    model: 'Indoor Talk',
    category: 'intercom',
    tagline: 'Audio answering unit — hardened-glass touch, PoE 802.3af, induction loop',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/indoor-stations/2n-indoor-talk/',
    specs: {
      type: 'ip-sip',
      side: 'indoor-monitor',
      poe: true,
    },
  },

  // SRC: https://www.2n.com/en-US/products/elevator-systems/2n-liftip-2/
  // Elevator emergency communicator, VoIP/SIP, PoE-powered, flush mounted in panel
  {
    id: '2n-liftip-2',
    manufacturer: '2N',
    model: 'LiftIP 2.0',
    category: 'intercom',
    tagline: 'Lift emergency IP communicator — VoIP, PoE, remote monitoring, plug-and-play',
    verified: true,
    datasheetUrl: 'https://www.2n.com/en-US/products/elevator-systems/2n-liftip-2/',
    specs: {
      type: 'ip-sip',
      side: 'outdoor-station',
      poe: true,
    },
  },
];
